import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCrmTenant } from '../crm/useCrmTenant';
import { CircularProgress } from '@mui/material';
import { esignApi } from '../../services';
import {
  CompletedEsignDetails,
  EsignFieldStatus,
  EsignSubmissionContext,
} from '../form-renderer/EsignSubmissionContext';
import CustomButton from './Button';

interface ESignButtonData {
  content?: React.ReactNode;
  field_name?: string;
  data?: {
    className?: string;
    field_name?: string;
    isRequired?: boolean;
    documentUrl?: string;
    type?: string;
    onSubmitCallback?: (() => void) | string;
    href?: string;
    openInNewTab?: boolean | string;
    preparePayment?: () => Promise<{
      leadId: string;
      formId: string;
      name: string;
      email: string;
      phone: string;
    }>;
    [key: string]: any;
  };
}

interface ESignButtonProps {
  data: ESignButtonData;
}

const TERMINAL_SUCCESS_STATUSES = ['SUCCESS', 'COMPLETED'];
const TERMINAL_FAILURE_STATUSES = ['DECLINED', 'REJECTED', 'EXPIRED'];
const STATUS_POLL_ATTEMPTS = 10;
const STATUS_POLL_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const ESignButton = React.forwardRef<HTMLButtonElement, ESignButtonProps>(
  (props, ref) => {
    const { tenantId } = useParams<{ tenantId?: string }>();
    // Gateway calls use the shared CRM tenant from the form URL.
    const crmTenantId = useCrmTenant();
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, content } = props.data;
    const esignSubmission = useContext(EsignSubmissionContext);

    const fieldName = data?.field_name || props.data.field_name || 'esign';
    const doneKey = `formEsign:${tenantId || 'unknown'}:${fieldName}:done`;
    const verificationKey = `formEsignVerification:${tenantId || 'unknown'}:${fieldName}`;
    const metaKey = `formEsignMeta:${tenantId || 'unknown'}:${fieldName}`;
    const pendingLeadKey = `formEsignPendingLead:${tenantId || 'unknown'}:${fieldName}`;
    const queryKey = `formEsignQuery:${tenantId || 'unknown'}:${fieldName}`;
    const pendingDocIdKey = `formEsignPendingDocId:${tenantId || 'unknown'}:${fieldName}`;

    const [localStatus, setLocalStatus] = useState<EsignFieldStatus>(
      sessionStorage.getItem(doneKey) === '1' ? 'done' : 'idle',
    );
    const [localErrorMessage, setLocalErrorMessage] = useState('');
    const externalStatus = esignSubmission?.getEsignStatus?.(fieldName);
    const status = externalStatus?.status ?? localStatus;
    const errorMessage = externalStatus?.errorMessage ?? localErrorMessage;

    const updateStatus = (next: EsignFieldStatus, message: string = '') => {
      setLocalStatus(next);
      setLocalErrorMessage(message);
      esignSubmission?.setEsignStatus?.(fieldName, {
        status: next,
        errorMessage: message,
      });
    };

    const actionButtonRef = useRef<HTMLButtonElement>(null);
    // Only auto-advance on the transition INTO 'done', not when the block
    // mounts already 'done' (e.g. user hit "Back" from a later step and
    // landed back on eSign) - otherwise it bounces straight forward again.
    const prevStatusRef = useRef<EsignFieldStatus>(status);

    const completeEsign = async (
      leadId: string,
      verificationId: string,
      docId?: string,
    ) => {
      sessionStorage.setItem(doneKey, '1');
      sessionStorage.removeItem(verificationKey);
      sessionStorage.removeItem(pendingLeadKey);
      sessionStorage.removeItem(pendingDocIdKey);
      const completed: CompletedEsignDetails = {
        fieldName,
        leadId,
        verificationId,
        ...(docId ? { docId } : {}),
      };
      sessionStorage.setItem(metaKey, JSON.stringify(completed));
      await esignSubmission?.completeEsign(fieldName, completed);
      updateStatus('done');
    };

    const pollStatus = async (
      verificationId: string,
      leadId: string,
      fallbackDocId?: string,
    ) => {
      updateStatus('processing');
      try {
        for (let attempt = 0; attempt < STATUS_POLL_ATTEMPTS; attempt += 1) {
          const result = await esignApi.getStatus(crmTenantId as string, verificationId);
          const resultStatus = String(result?.status || '').toUpperCase();
          const docId =
            result?.docId != null
              ? String(result.docId)
              : result?.doc_id != null
                ? String(result.doc_id)
                : fallbackDocId;

          if (TERMINAL_SUCCESS_STATUSES.includes(resultStatus)) {
            await completeEsign(leadId, verificationId, docId);
            return;
          }
          if (TERMINAL_FAILURE_STATUSES.includes(resultStatus)) {
            updateStatus('error', `eSign was not completed (${resultStatus}).`);
            return;
          }
          await sleep(STATUS_POLL_DELAY_MS);
        }
        updateStatus('error', 'Timed out waiting for eSign to complete.');
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to check eSign status.';
        updateStatus('error', String(message));
      }
    };

    useEffect(() => {
      const returnedFromRedirect = searchParams.get('journeyReturn') === 'esign';
      const storedVerificationId = sessionStorage.getItem(verificationKey);
      const storedLeadId = sessionStorage.getItem(pendingLeadKey) || '';
      const storedDocId = sessionStorage.getItem(pendingDocIdKey) || undefined;

      if (!returnedFromRedirect) return;

      // Put the form's original query params back (dropping the eSign marker)
      // once the result is handled.
      const restoreQuery = () => {
        let saved = '';
        try {
          saved = sessionStorage.getItem(queryKey) || '';
          sessionStorage.removeItem(queryKey);
        } catch {
          saved = '';
        }
        setSearchParams(new URLSearchParams(saved), { replace: true });
      };

      if (storedVerificationId) {
        pollStatus(storedVerificationId, storedLeadId, storedDocId).finally(restoreQuery);
      } else {
        restoreQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (status === 'done' && prevStatusRef.current !== 'done') {
        actionButtonRef.current?.click();
      }
      prevStatusRef.current = status;
    }, [status]);

    const startSigning = async () => {
      if (!crmTenantId) {
        updateStatus('error', 'Missing tenant context for eSign.');
        return;
      }
      if (!data?.documentUrl) {
        updateStatus('error', 'No document configured for eSign.');
        return;
      }

      updateStatus('processing');

      try {
        const prepareLead = data?.preparePayment || esignSubmission?.prepareLead;
        if (!prepareLead) {
          throw new Error(
            'eSign is unavailable in the builder preview. Save the form and open its View page to test eSign.',
          );
        }
        const customer = await prepareLead();

        const documentResponse = await fetch(data.documentUrl);
        if (!documentResponse.ok) {
          throw new Error('Failed to fetch the document to sign.');
        }
        const documentBlob = await documentResponse.blob();

        // Keep redirect_url short: the form's query params are stashed in
        // sessionStorage and restored when the signer comes back.
        sessionStorage.setItem(queryKey, window.location.search.replace(/^\?/, ''));
        const returnUrl = new URL(window.location.origin + window.location.pathname);
        returnUrl.searchParams.set('journeyReturn', 'esign');

        const formData = new FormData();
        formData.append('document', documentBlob, 'document.pdf');
        formData.append('signer_name', customer.name);
        formData.append('signer_email', customer.email);
        formData.append('signer_phone', customer.phone);
        formData.append('lead_id', customer.leadId);
        formData.append('pages', 'all');
        formData.append('redirect_url', returnUrl.toString());

        const result = await esignApi.signDocument(crmTenantId, formData);
        const verificationId = result?.verification_id || result?.verificationId;
        const signUrl = result?.sign_url || result?.signUrl;
        const docId =
          result?.docId != null
            ? String(result.docId)
            : result?.doc_id != null
              ? String(result.doc_id)
              : undefined;

        if (!verificationId || !signUrl) {
          throw new Error('Failed to initiate eSign (missing verification ID or sign URL).');
        }

        sessionStorage.setItem(verificationKey, verificationId);
        sessionStorage.setItem(pendingLeadKey, customer.leadId);
        if (docId) sessionStorage.setItem(pendingDocIdKey, docId);
        window.location.href = signUrl;
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Failed to start eSign.';
        updateStatus('error', String(message));
      }
    };

    const hiddenActionButton = (
      <div style={{ display: 'none' }}>
        <CustomButton
          ref={actionButtonRef}
          data={{
            data: {
              type: data?.type,
              onSubmitCallback: data?.onSubmitCallback,
              href: data?.href,
              openInNewTab: data?.openInNewTab,
              submissionResponse: (data as any)?.submissionResponse,
            } as any,
            content: '',
          }}
        />
      </div>
    );

    if (status === 'done') {
      return (
        <div className={data?.className || 'btn d-flex'} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          <span>Your document is signed.</span>
          <CustomButton
            data={{
              className: 'btn d-flex',
              onClick: () => actionButtonRef.current?.click(),
              data: { type: 'button' },
              content: 'Continue',
            }}
          />
          {hiddenActionButton}
        </div>
      );
    }

    const buttonLabel =
      status === 'processing'
        ? 'Processing...'
        : typeof content === 'string'
          ? content
          : content || 'Sign Document';

    return (
      <div>
        <CustomButton
          ref={ref}
          data={{
            className: data?.className || 'btn d-flex ',
            disabled: status === 'processing',
            onClick: startSigning,
            data: { type: 'button' },
            content:
              status === 'processing' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                  {buttonLabel}
                </span>
              ) : (
                buttonLabel
              ),
          }}
        />
        {hiddenActionButton}
        {errorMessage && (
          <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: 4 }}>
            {errorMessage}
          </div>
        )}
      </div>
    );
  },
);

ESignButton.displayName = 'ESignButton';

export default ESignButton;
